import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Grid,
  Checkbox,
  Box,
  Typography,
  Card,
  Autocomplete,
  TextField,
} from "@mui/material";
import { keyframes } from "@mui/system";

// Custom components
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Encryption utils
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";
import MedicalAbstractBackground from "./MedicalAbstractBackground ";

// Dummy Custom Stepper Component
const CustomStepper = ({ title }) => {
  return (
    <Box mb={3} display="flex" justifyContent="center">
      <Typography
        variant="h6"
        sx={{ fontWeight: "bold", color: "#2e7fae", textAlign: "center" }}
      >
        {title}
      </Typography>
    </Box>
  );
};

function CreateDevicemapping() {
  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const userid = loginData?.userid;

  const date = new Date();
  let day = date.getDate().toString().padStart(2, "0");
  let month = (date.getMonth() + 1).toString().padStart(2, "0");
  let year = date.getFullYear();
  let currentDate = `${year}-${month}-${day}`;

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Patients
  const [patients, setPatients] = useState([]);
  const [patientInput, setPatientInput] = useState("");

  // Devices
  const [devices, setDevices] = useState([]);
  const [deviceInput, setDeviceInput] = useState("");

  // Mapping details
  const [mappingDetails, setMappingDetails] = useState({
    ccid: userid,
    assignedDate: currentDate,
    patient: null, 
    patientid: "",
    deviceid: [],
  });

  // 🔹 Fetch patients
  const fetchPatients = async (searchTerm = "") => {
    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      let response;
      if (searchTerm.trim()) {
        response = await axios.post(
          `/api/patientregistration/searchByCcid/${userid}`,
          { searchTerm },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.get(
          `/api/patientregistration/ccid/nopagination/${userid}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

     if (response.status === 200) {
       const decrypted = decryptBody(response.data, secretKey);
       console.log("👨‍⚕️ Raw Patients Response:", response.data);
       console.log("🔓 Decrypted Patients:", decrypted);
     
       let patientsArray = [];
     
       if (Array.isArray(decrypted)) {
         patientsArray = decrypted; // no-pagination API
       } else if (decrypted?.content && Array.isArray(decrypted.content)) {
         patientsArray = decrypted.content; // search API
       }
     
       console.log("✅ Patients Array Length:", patientsArray.length);
       setPatients(patientsArray);
     }
       } catch (err) {
         console.error("Error fetching patients:", err);
       } finally {
         setLoading(false);
       }
     };

  // 🔹 Fetch devices
  const fetchDevices = async (searchTerm = "") => {
    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      let response;
      if (searchTerm.trim()) {
        response = await axios.post(
          `/api/device/searchByCcid/${userid}`,
          { searchTerm },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.get(`/api/device/ccid/${userid}/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
        setDevices(Array.isArray(decrypted) ? decrypted : []);
      }
    } catch (err) {
      console.error("Error fetching devices:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch patients on load
  useEffect(() => {
    fetchPatients();
  }, []);

  // Patient search debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (patientInput.trim()) {
        fetchPatients(patientInput);
      } else {
        fetchPatients("");
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [patientInput]);

  // Device search debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchDevices(deviceInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [deviceInput]);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mappingDetails.patientid) {
      toast.error("Please select a patient.");
      return;
    }
    if (!mappingDetails.deviceid || mappingDetails.deviceid.length === 0) {
      toast.error("Please select at least one device.");
      return;
    }

    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      const mappingList = mappingDetails.deviceid.map((devId) => ({
        ccid: userid,
        assignedDate: mappingDetails.assignedDate,
     patientid: mappingDetails.patient.id,
        deviceid: devId,
      }));

      const rawPayload = { mapping: mappingList };
      const encryptedData = encryptBody(rawPayload, secretKey);

      const response = await axios.post(
        "/api/devicepatientmapping/create",
        encryptedData,
        {
          headers: {
            "Content-Type": "text/plain",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
        toast.success(
          decrypted?.message || "Device Mapping saved successfully!"
        );
        navigate(-1);
      }
    } catch (err) {
     console.error("Error saving mapping:", err);
  toast.error(err?.response?.data?.message);
  } finally {
    setLoading(false);
  }
};

  // Spinner animation
  const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;
  const dotPositions = [
    { top: "5%", left: "50%" },
    { top: "20%", left: "80%" },
    { top: "50%", left: "95%" },
    { top: "80%", left: "80%" },
    { top: "95%", left: "50%" },
    { top: "80%", left: "20%" },
    { top: "50%", left: "5%" },
    { top: "20%", left: "20%" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MedicalAbstractBackground/>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 80,
              height: 80,
              animation: `${spinAnimation} 1.5s linear infinite`,
            }}
          >
            {dotPositions.map((pos, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1A237E",
                  position: "absolute",
                  ...pos,
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Card
        sx={{ width: "1000px", maxWidth: "1200px", padding: 3, margin: "auto" }}
      >
        <MDBox pt={4} pb={3} px={3}>
          {/* Stepper Title */}
          <CustomStepper title="Device-Patient Mapping" />

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* 🔹 Patient Dropdown with Search */}
              <Grid item xs={6}>
               <Autocomplete
  options={Array.isArray(patients) ? patients : []}
  getOptionLabel={(option) =>
    option ? `${option.firstname} ${option.lastname}` : ""
  }
  value={mappingDetails.patient} // whole object
  onChange={(e, newValue) => {
    console.log("🖱️ Selected patient:", newValue);
    setMappingDetails((prev) => ({
      ...prev,
      patient: newValue,           
      patientid: newValue?.id || "",
    }));
  }}
  inputValue={patientInput}
  onInputChange={(e, newInputValue, reason) => {
    console.log(
      "⌨️ Input field updated:",
      newInputValue,
      "| Reason:",
      reason
    );

    if (reason === "input") {
      setPatientInput(newInputValue);
    } else if (reason === "reset") {
      // ✅ Show full selected name
      const fullName =
        newInputValue ||
        (mappingDetails.patient
          ? `${mappingDetails.patient.firstname} ${mappingDetails.patient.lastname}`
          : "");

      console.log("🔄 Reset triggered, setting full name:", fullName);
      setPatientInput(fullName);
    }
  }}
  renderInput={(params) => (
    <TextField {...params} label="Select Patient"       
                    placeholder="search or select patient"
 variant="outlined" />
  )}
  freeSolo={false}
/>
              </Grid>

              {/* 🔹 Device Dropdown with Multi-Select */}
              <Grid item xs={6}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={Array.isArray(devices) ? devices : []}
                  getOptionLabel={(option) =>
                    option ? option.devicename || option.name : ""
                  }
                  value={
                    Array.isArray(devices)
                      ? devices.filter((dev) =>
                          mappingDetails.deviceid.includes(dev.id)
                        )
                      : []
                  }
                  onChange={(e, newValue) =>
                    setMappingDetails({
                      ...mappingDetails,
                      deviceid: newValue.map((dev) => dev.id),
                    })
                  }
                  inputValue={deviceInput}
                  onInputChange={(e, newInputValue) => {
                    if (e?.type === "change") {
                      setDeviceInput(newInputValue);
                    }
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} />
                      {option.devicename || option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Device"
                      variant="outlined"
                      placeholder="search or select device"
                    />
                  )}
                />
              </Grid>

              {/* 🔹 Assign Date */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Assign Date"
                  InputLabelProps={{ shrink: true }}
                  value={mappingDetails.assignedDate}
                  onChange={(e) =>
                    setMappingDetails({
                      ...mappingDetails,
                      assignedDate: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>

            {/* 🔹 Buttons */}
            <MDBox mt={3} display="flex" justifyContent="space-between">
              <MDButton
                variant="outlined"
                onClick={() => navigate(-1)}
                style={{
                  backgroundColor: "#1A237E",
                  color: "#fff",
                  textTransform: "none",
                }}
              >
                Cancel
              </MDButton>

              <MDButton
                type="submit"
                variant="contained"
                style={{
                  backgroundColor:"#1A237E",
                  color: "#fff",
                  textTransform: "none",
                }}
              >
                Submit
              </MDButton>
            </MDBox>
          </form>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
}

export default CreateDevicemapping;
